import cloneDeep from 'lodash.clonedeep';
import React from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

export type CommonRouteObject = RouteObject & {
  handle?: {
    name?: ((params?: any) => string) | string; // 菜单展示文案
    isMenu?: boolean; // 是否注册为菜单
    icon?: React.ReactNode; // 菜单icon
    permission?: string[]; // 权限
    disabled?: boolean;
    title?: string; // 页面标题
  };
};
export interface BreadcrumbItem {
  url?: string;
  text: string;
}
export type MenuItem = {
  key: string;
  label?: any;
  icon: React.ReactNode;
  disabled?: boolean;
  children?: MenuItem[];
};
export type ParsedType = {
  menuList: MenuItem[];
  routerList: RouteObject[];
  breadcrumbData: { [key: string]: BreadcrumbItem[] };
};
// 处理路由权限，重定向等
export function parseRouteConfigs(
  configs: CommonRouteObject[],
  checkPermission: (permission?: string[]) => boolean,
  unAuthPage: React.ReactNode,
  parsed: ParsedType = {
    menuList: [],
    routerList: [],
    breadcrumbData: {},
  },
  parentPath = '',
  breadcrumbList: BreadcrumbItem[] = []
) {
  const menuList: MenuItem[] = [];
  const routerList: RouteObject[] = [];
  // 记录第一个有权限的子路由
  let firstRouteHasAuth: CommonRouteObject | undefined;
  // 记录需要重定向的路由
  let indexRouteWithNoEle: CommonRouteObject | undefined;

  configs.forEach((conf) => {
    conf.handle = conf.handle || {};
    const { permission, name } = conf.handle || {};
    const path = (parentPath + (conf.path && conf.path !== '/' ? `/${conf.path}` : '')) as string;

    // 处理面包屑
    let _breadcrumbList = breadcrumbList;
    if (name !== undefined) {
      _breadcrumbList = breadcrumbList.concat({
        text: name,
        url: path,
      });
    }
    // 记录路由全路径，便于业务中自定义面包屑时寻找
    conf.handle.path = path;
    parsed.breadcrumbData[path] = _breadcrumbList;

    // 检测第一层权限， 当前路由无权限，直接跳过，也不展示menu
    if (!checkPermission(permission)) {
      conf.handle.hasAuth = false;
      conf.element = unAuthPage;
      if (conf.handle.isMenu) conf.handle.isMenu = false;
      routerList.push(conf);
      return;
    }
    conf.handle.hasAuth = true;
    // 处理菜单
    const menu: MenuItem = {
      key: path,
      label: conf.handle.name,
      icon: conf.handle.icon,
      disabled: conf.handle.disabled,
    };
    if (conf.children?.length) {
      const { parentHasAuth, menuList: subMenuList } = parseRouteConfigs(
        conf.children || [],
        checkPermission,
        unAuthPage,
        parsed,
        path,
        _breadcrumbList
      );
      // 根据业务处理403页面或者将children置空，同时将设为菜单的项目隐藏菜单， hasAuth为false;
      if (!parentHasAuth) {
        conf.handle.hasAuth = false;
        conf.element = unAuthPage;
        if (conf.handle.isMenu) conf.handle.isMenu = false;
      } else {
        if (subMenuList.length) {
          menu.children = subMenuList;
        }
      }
    }
    if (conf.handle.isMenu) {
      menuList.push(menu);
    }

    // 记录第一个有权限的子路由
    if (conf.handle.hasAuth && !firstRouteHasAuth && !conf.index) {
      firstRouteHasAuth = conf;
    }
    // 记录配置为index的路由，且不配置element的路由
    if (conf.index && !conf.element && !indexRouteWithNoEle) {
      indexRouteWithNoEle = conf;
    }
    routerList.push(conf);
  });
  // 处理配置为index的路由，且不配置element的路由，使用同层级第一个有权限的路由作为重定向
  if (indexRouteWithNoEle) {
    if (firstRouteHasAuth) {
      indexRouteWithNoEle.element = React.createElement(Navigate, {
        to: firstRouteHasAuth.path || '',
      });
    } else {
      indexRouteWithNoEle.handle.hasAuth = false;
    }
  }

  if (menuList?.length) {
    parsed.menuList = menuList;
  }
  parsed.routerList = routerList;
  // 只要有一个路由检测为有权限，则判定该层级父级路由拥有权限
  return {
    configs,
    parentHasAuth: configs.some((conf) => conf.handle.hasAuth),
    menuList,
    parsed,
  };
}
// 生成权限路由
const parseRoutes = (
  routes: CommonRouteObject[],
  checkPermission: (permission?: string[]) => boolean,
  unAuthPage?: React.ReactNode
) => {
  const { parsed } = parseRouteConfigs(cloneDeep(routes), checkPermission, unAuthPage || '403');
  return parsed;
};
export default parseRoutes;
